import { config } from "../config";
import { t } from "../locale";
import type { Viewer } from "./Viewer";

export class ViewerError extends Error {
  constructor(
    message: string,
    public status: number,
    public code = "",
  ) {
    super(message);
  }
}
export function clientContext() {
  return {
    platform:
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        ? "iOS"
        : /Android/.test(navigator.userAgent)
          ? "Android"
          : "desktop",
    displayMode:
      matchMedia("(display-mode: standalone)").matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
        ? "standalone"
        : "browser",
  };
}
export async function deviceRequest<T>(
  path: string,
  method = "GET",
  body?: unknown,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), path === "account/email/code" ? 30000 : 10000);
  try {
    const response = await fetch(`${config.apiBaseUrl}/v1/pwa/${path}`, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      keepalive: path === "library" && method === "PUT",
    });
    const data = await response.json();
    if (!response.ok)
      throw new ViewerError(
        data.error?.message || t("Could not connect to your account."),
        response.status,
        data.error?.code,
      );
    return data;
  } catch (error) {
    if (error instanceof ViewerError) throw error;
    throw new ViewerError(t("Could not connect to your account."), 0);
  } finally {
    clearTimeout(timer);
  }
}

let pending: Promise<Viewer> | null = null;
export function resetDeviceAccount() { pending = null; }
export function ensureDeviceAccount(): Promise<Viewer> {
  if (pending) return pending;
  const bootstrap = async () => {
    // The parent finishes account setup before rendering its same-origin player.
    if (window.top !== window.self) return deviceRequest<Viewer>("account/me");
    for (let attempt = 0; attempt < 2; attempt++) {
      const data = await deviceRequest<{
        cookieRequired: boolean;
        environment: string;
        user?: Viewer;
      }>("account/device", "POST", clientContext());
      if (data.environment !== config.environment)
        throw new Error(t("Account environment mismatch."));
      if (!data.cookieRequired && data.user?.userId && data.user.deviceId)
        return data.user;
    }
    throw new Error(
      t("Allow this site to save data so your account can be created."),
    );
  };
  // Serialize first-time setup across tabs, so they share one server-issued cookie.
  const locked = async () =>
    navigator.locks
      ? await navigator.locks.request(
          `yestv:${config.environment}:device-setup`,
          bootstrap,
        )
      : await bootstrap();
  pending = locked().catch((error) => {
    pending = null;
    throw error;
  });
  return pending;
}
